/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import {
  SkillLevel,
  SkillLevelFeatures,
  SKILL_LEVEL_STORAGE_KEY,
  SKILL_LEVEL_FEATURES,
  SKILL_LEVEL_FRET_DEFAULTS,
} from '../types/skillLevel';

interface SkillLevelContextValue {
  /** Current skill level */
  skillLevel: SkillLevel;
  /** Update the skill level */
  setSkillLevel: (level: SkillLevel) => void;
  /** Feature flags based on current skill level */
  features: SkillLevelFeatures;
  /** Default fret range for current skill level */
  defaultFretRange: { min: number; max: number };
  /** Whether this is the user's first visit (no stored skill level) */
  isFirstVisit: boolean;
}

const SkillLevelContext = createContext<SkillLevelContextValue | null>(null);

interface SkillLevelProviderProps {
  children: ReactNode;
}

export const SkillLevelProvider: React.FC<SkillLevelProviderProps> = ({ children }) => {
  const [isFirstVisit, setIsFirstVisit] = useState<boolean>(() => {
    return localStorage.getItem(SKILL_LEVEL_STORAGE_KEY) === null;
  });

  const [skillLevel, setSkillLevelState] = useState<SkillLevel>(() => {
    const stored = localStorage.getItem(SKILL_LEVEL_STORAGE_KEY);
    if (stored === 'beginner' || stored === 'intermediate' || stored === 'advanced') {
      return stored;
    }
    // First-time user defaults to beginner
    return 'beginner';
  });

  // Persist skill level changes
  useEffect(() => {
    localStorage.setItem(SKILL_LEVEL_STORAGE_KEY, skillLevel);
    // Once user has set a skill level, they're no longer a first-time visitor
    if (isFirstVisit) {
      setIsFirstVisit(false);
    }
  }, [skillLevel, isFirstVisit]);

  const setSkillLevel = (level: SkillLevel) => {
    setSkillLevelState(level);
  };

  const features = useMemo(() => SKILL_LEVEL_FEATURES[skillLevel], [skillLevel]);
  const defaultFretRange = useMemo(() => SKILL_LEVEL_FRET_DEFAULTS[skillLevel], [skillLevel]);

  const value = useMemo(
    () => ({
      skillLevel,
      setSkillLevel,
      features,
      defaultFretRange,
      isFirstVisit,
    }),
    [skillLevel, features, defaultFretRange, isFirstVisit]
  );

  return (
    <SkillLevelContext.Provider value={value}>
      {children}
    </SkillLevelContext.Provider>
  );
};

/**
 * Hook to access skill level context.
 * Must be used within a SkillLevelProvider.
 */
export const useSkillLevel = (): SkillLevelContextValue => {
  const context = useContext(SkillLevelContext);
  if (!context) {
    throw new Error('useSkillLevel must be used within a SkillLevelProvider');
  }
  return context;
};

export default SkillLevelContext;
